import { A11y, Scrollbar } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Box, Card, Flex, Heading, Tag, TagLabel, TagRightIcon, useBreakpointValue, useToken } from '@chakra-ui/react';
import { ArrowRightIcon, TimeIcon } from '@udacity/chakra-uds-icons';

import { ButtonLink } from '~/components/button-link';
import { ContentContainer } from '~/components/container';
import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';
import { Markdown } from '~/components/markdown';
import { SanityImage } from '~/components/sanity-image';
import { trackEvent } from '~/features/analytics/track-event';
import { format, useFormatDate } from '~/hooks/use-format-date';

import { MarketingEvent } from '../models/marketing-event';

import 'swiper/css';
import 'swiper/css/scrollbar';

export type UpcomingEventsSectionProps = {
  title: string;
  description?: string;
  events: MarketingEvent[];
};

export function UpcomingEventsSection({ title, description, events }: UpcomingEventsSectionProps) {
  const [containerWidth] = useToken('sizes', ['content-max-width']);
  const swiperKey = useBreakpointValue({ base: 'base', md: 'md' });
  if (!events?.length) return null;
  return (
    <Box
      as='section'
      bgGradient='linear(90deg, rgba(98, 187, 255, 0.12) 0%, rgba(0, 106, 255, 0.12) 100%)'
      overflow='hidden'
      padding={{
        base: '40px 0px',
        md: '80px 0px',
      }}
    >
      <ContentContainer>
        <Box
          textAlign='center'
          mb={{
            base: 8,
            md: 10,
          }}
        >
          <Heading
            as='h2'
            size={{
              base: 'marketing-2xl',
              md: 'marketing-5xl',
            }}
          >
            {title}
          </Heading>
          {!!description && (
            <HeadingWithEmphasis
              text={description}
              emphasisProps={{
                background: 'linear-gradient(89.43deg, #62BBFF 73.65%, #006AFF 97.67%)',
                backgroundClip: 'text',
                sx: {
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                },
                fontWeight: '500',
              }}
              headingProps={{
                as: 'p',
                size: {
                  base: 'marketing-lg',
                  md: 'marketing-xl',
                },
              }}
            />
          )}
        </Box>
      </ContentContainer>
      <Box
        sx={{
          '.uds-carousel .swiper-scrollbar-drag': {},
          '.uds-carousel': {
            paddingTop: '0px',
            paddingBottom: {
              md: events.length < 3 ? 'unset' : '60px',
            },
            paddingX: {
              base: '16px',
              md: `calc((100vw - ${containerWidth}) / 2)`,
            },
          },
          '.uds-carousel .swiper-wrapper': {
            gap: 'unset',
            justifyContent: {
              md: events.length < 3 ? 'center' : 'unset',
            },
          },
          '.uds-carousel .swiper-slide:not(:last-child)': {
            marginRight: '16px !important',
          },
          '.uds-carousel .swiper-scrollbar': {
            top: 'unset',
            right: '50%',
            left: 'unset',
            bottom: 0,
            transform: 'translateX(50%)',
          },
          '.uds-carousel--rtl .swiper-scrollbar': {
            right: 'unset',
            left: 'unset',
          },
        }}
      >
        <Swiper
          key={swiperKey}
          className='uds-carousel'
          direction='horizontal'
          freeMode={true}
          grabCursor={true}
          modules={[A11y, Scrollbar]}
          scrollbar={{ draggable: true }}
          slidesPerView='auto'
        >
          {events.map((event) => (
            <SwiperSlide key={event._id}>
              <EventCard event={event} />
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>
    </Box>
  );
}

type EventCardProps = {
  event: MarketingEvent;
};

function EventCard({ event }: EventCardProps) {
  const formattedDatetime = useFormatDate({
    format: format.weekdayMonthDayAtTimeZone,
    date: event.startDatetime,
    timezone: 'America/Los_Angeles',
  });

  return (
    <Card
      as='article'
      borderRadius='12px'
      display='flex'
      flexDirection='column'
      gap={6}
      padding={4}
      position='relative'
      _before={{
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: 'inherit',
        padding: '1px',
        background: 'linear-gradient(90deg, #62BBFF 0%, #006AFF 100%)',
        mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        maskComposite: 'exclude',
        pointerEvents: 'none',
      }}
      width={{
        base: '293px',
        md: '356px',
      }}
    >
      <Box
        borderRadius='8px'
        overflow='hidden'
        position='relative'
        width='100%'
        height={{
          base: '144px',
          md: '200px',
        }}
      >
        {event.thumbnailImage && (
          <SanityImage
            alt={event.thumbnailImageAltText}
            image={event.thumbnailImage}
            loading='lazy'
            style={{
              display: 'block',
              objectFit: 'contain',
              backgroundColor: '#000',
            }}
            fill
          />
        )}
      </Box>
      <Flex flexDir='column' gap={4} justifyContent='space-between'>
        <Box
          overflow='hidden'
          flex={{
            base: '0 0 156px',
            md: '0 0 108px',
          }}
        >
          <Markdown
            markdownText={event.eventDescription}
            components={{
              h3: ({ children }) => (
                <Heading as='h3' size='marketing-xl'>
                  {children}
                </Heading>
              ),
            }}
          />
        </Box>
        <Flex alignItems='flex-start' flexDirection='column-reverse' gap={4} justifyContent='space-between'>
          <ButtonLink
            linkProps={{ href: event.registrationUrl }}
            buttonProps={{
              variant: 'solid',
              size: 'md',
              rightIcon: <ArrowRightIcon height='20px' width='20px' />,
              width: 'fit-content',
              onClick: () =>
                trackEvent({
                  type: 'click',
                  name: 'Agentic AI Page CTA Clicked',
                  label: 'save_your_spot_clicked',
                  value: event.eventTitle,
                }),
            }}
          >
            Save Your Spot
          </ButtonLink>
          <Tag
            bgGradient='linear(180deg, #F3F6F9 0%, #E3EBF1 100%)'
            border='1px solid #AEB8CD33'
            boxShadow='1px 1px 2px 0px #FFFFFFBF inset'
          >
            <TagLabel color='#454545' fontWeight='600'>
              {formattedDatetime}
            </TagLabel>
            <TagRightIcon as={TimeIcon} />
          </Tag>
        </Flex>
      </Flex>
    </Card>
  );
}
